<template>
  <v-container class="pa-0 overflow-hidden">
    <v-row class="pa-0 ma-0 mt-4">
      <v-col cols="12" sm="12" :md="mdCols" class="pa-0 ma-0">
        <SingleSelectionAutocomplete
          data-private
          itemValue="id"
          :tourId="siteTourId"
          :cacheItems="false"
          :class="verticalLayout ? '' : 'me-3'"
          :label="$t('common.site_one')"
          :options="siteOptions"
          :value="location.site"
          :loading="loading"
          @change="onChangeSite"
        />
      </v-col>
      <v-col cols="12" sm="12" :md="mdCols" class="pa-0 ma-0">
        <MultipleSelect
          data-private
          itemValue="id"
          :cacheItems="false"
          :label="$t('common.tag_other')"
          :options="tagsOptions"
          :showSelectAll="false"
          :value="location.tags"
          :loading="loading"
          @change="onChangeTags"
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import SingleSelectionAutocomplete from './SingleSelectionAutocomplete.vue'
import MultipleSelect from '@/components/Core/MultipleSelect.vue'
import { mapGetters } from 'vuex'

import { utils } from 'frontend-js-shared'

import api from '@/api'
import mixin from '@/mixins/mixin.vue'
import _ from 'lodash'

export default {
  name: 'LocationSelector',

  mixins: [mixin],

  components: {
    SingleSelectionAutocomplete,
    MultipleSelect,
  },

  props: {
    siteTourId: String,
    organizationId: { type: String, required: true },
    location: {
      type: Object,
      required: false,
      default() {
        return {
          site: '',
          tags: [],
        }
      },
    },
    verticalLayout: {
      type: Boolean,
      default: false,
    },
    availableTagIds: {
      type: Array,
    },
    availableSiteIds: {
      type: Array,
    },
  },

  data() {
    return {
      loading: false,
      tags: [],
      sites: [],
    }
  },

  watch: {
    'location.site': {
      immediate: true,
      handler(newSite) {
        if (this.loading) return // When loading org structure for an org different to the user's (ex. rever admins managing users from other orgs), we don't want to emit the change event until the org structure is loaded, otherwise selectedTags can't be computed, resulting in an empty selector, even if the user has tags assigned.
        if (newSite) this.onChangeSite(newSite)
      },
    },
  },

  created() {
    if (!this.isOwnOrganization) {
      this.getOrganizationStructure()
    }
  },

  computed: {
    ...mapGetters({
      organizationSites: 'organizationSites',
      organizationTags: 'organizationTags',
      accountOrganizationId: 'organizationId',
    }),

    isOwnOrganization() {
      return this.organizationId === this.accountOrganizationId
    },

    siteOptions() {
      const sites = this.isOwnOrganization ? this.organizationSites : this.sites
      const visibleSites = _.isEmpty(this.availableSiteIds)
        ? sites
        : sites.filter(site =>
            this.availableSiteIds.includes(utils.getEntityId(site))
          )
      return visibleSites.map(site => {
        return {
          ...site,
          subtitle: this.$t(`countries.${site.countryCodes[0]}`),
          subtitleIcon: 'mdi-earth',
        }
      })
    },

    siteOrGlobalTags() {
      const tags = this.isOwnOrganization ? this.organizationTags : this.tags
      return tags?.filter?.(tag => {
        const tagSites = tag?.sites ?? []
        return tagSites.includes(this.location.site) || tag?.sites?.length === 0
      })
    },

    tagsOptions() {
      if (_.isEmpty(this.availableTagIds)) {
        return _.sortBy(this.siteOrGlobalTags, 'name') ?? []
      }

      const organizationTagsMap = new Map(
        this.organizationTags.map(tag => [tag.id, tag])
      )

      return _.chain(this.availableTagIds)
        .map(tagId => organizationTagsMap.get(tagId))
        .compact()
        .sortBy('name')
        .value()
    },

    mdCols() {
      return this.verticalLayout ? '12' : '6'
    },

    selectedTags() {
      return (
        this.location.tags.filter(locationTag => {
          const tagId = _.isObject(locationTag) ? locationTag?.id : locationTag

          return this.tagsOptions.some(
            tag =>
              tag?.id === tagId &&
              (tag?.sites?.length === 0 ||
                tag?.sites?.includes(this.location.site))
          )
        }) ?? []
      )
    },
  },

  methods: {
    async getOrganizationStructure() {
      this.loading = true
      try {
        const response = await api.organization.getOrganizationStructure(
          this.organizationId
        )
        this.sites = response?.data?.sites ?? []
        this.tags = response?.data?.tags ?? []
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.loading = false
      }
    },

    onChangeSite(id) {
      this.$emit('change', {
        site: id === null ? '' : id,
        tags: this.selectedTags,
      })
    },

    onChangeTags(value = []) {
      this.$emit('change', {
        ...this.location,
        tags: value,
      })
    },
  },
}
</script>
